import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Music, ArrowRight } from "lucide-react";

const BG_PHOTO = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/ph0o6ilo_BG.JPG";

export default function MusicPage() {
  useEffect(() => {
    document.title = "BuffaloGrass — Americana Roots Music | Donald Stephen Inbody";
  }, []);

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="BuffaloGrass — Americana Roots Music"
        description="BuffaloGrass — heartland Americana roots music by Donald Stephen Inbody. Rooted in the sounds of the Great Plains, blending folk, country, jazz, and bluegrass. Don holds music degrees from Wichita State University and has sung in choirs from Kansas schoolrooms to the U.S. Naval Academy."
        canonical="https://inbody.net/music"
        keywords="BuffaloGrass, Americana music, roots music, Donald Stephen Inbody music, heartland country, Wichita State University music education, Scott Byars, Kansas folk country bluegrass, Naval Academy Glee Club"
      />
      <Navbar />

      {/* Hero — BG photo as background */}
      <section
        data-testid="band-photo-section"
        className="relative min-h-[65vh] flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: `url(${BG_PHOTO})`, backgroundPosition: "center 25%" }}
        />
        {/* Gradient: transparent at top, dark at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/85 via-[#0A1628]/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-16 pt-32 w-full">
          <p className="overline text-[#C5A059] mb-3">Americana Roots Music</p>
          <h1 data-testid="music-page-title" className="font-serif text-5xl font-black text-[#F5F0E8] mb-4">
            BuffaloGrass
          </h1>
          <p className="text-[#F5F0E8]/75 max-w-lg leading-relaxed">
            Heartland Americana with deep roots, honest lyrics, and acoustic soul.
          </p>
        </div>
      </section>

      {/* About */}
      <section data-testid="band-about-section" className="py-16 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="overline mb-3">The Band</p>
              <h2 className="font-serif text-3xl font-bold text-[#0A1628] mb-6">
                Music from the Plains
              </h2>
              <div className="space-y-4 text-[#2D3142] leading-relaxed">
                <p>
                  I have been involved in music my entire life. Starting young, I sang in children's choirs. Then, as I got older, I sang in school choirs up through High School. My High School Choir director, Dr. Don Donaldson, at Shawnee Mission South High School, encouraged me to go to college at Wichita State University where I earned a Bachelor and Masters Degree in Music Education. I subsequently taught K-12 Vocal music in Kansas (Stafford and Ness City), then two years at Kansas Newman College.
                </p>
                <p>
                  Following that, I joined the Navy and temporarily put my music on hold. However, by the time I was assigned to teach at the Naval Academy, I became the Officer Representative to the USNA Men's Glee Club, then under Dr. Barry Talley, a great musician. Three years with them rekindled my interest.
                </p>
                <p>
                  I have, since then, been regularly singing in church choirs and directing them when the need arises.
                </p>
                <p>
                  In 2004, I joined with Scott Byars and Howard Surber to form the band BuffaloGrass. Howard has since moved on, but the band continues. We are an Americana roots band rooted in the sounds and stories of the American heartland. Drawing inspiration from the wide-open spaces of the Great Plains, the music weaves together folk, country, jazz, and bluegrass.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="overline mb-3">Sound &amp; Style</p>
              {[
                { genre: "Americana Roots", desc: "Rooted in the folk and country traditions of the American plains." },
                { genre: "Acoustic Ensemble", desc: "Fiddle, mandolin, guitar, bass — honest instruments, honest sound." },
                { genre: "Original Songwriting", desc: "Stories from the heartland — love, loss, open road, and home." },
              ].map(({ genre, desc }) => (
                <div key={genre} className="bento-card bg-white p-5 flex gap-4">
                  <div className="w-0.5 bg-[#C5A059] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-serif font-semibold text-[#0A1628] mb-1">{genre}</h4>
                    <p className="text-[#2D3142] text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Shows */}
      <section data-testid="shows-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1 bg-[#0A1628]/15" />
            <p className="overline">Upcoming Shows</p>
            <div className="h-px flex-1 bg-[#0A1628]/15" />
          </div>
          <div data-testid="shows-placeholder" className="text-center py-12 border border-[#0A1628]/10">
            <Music size={28} className="text-[#C5A059] mx-auto mb-4" />
            <h3 className="font-serif text-xl font-bold text-[#0A1628] mb-2">
              Show Dates Coming Soon
            </h3>
            <p className="text-[#2D3142] text-sm leading-relaxed max-w-md mx-auto mb-6">
              BuffaloGrass has upcoming performances in the works. Subscribe to the newsletter to be the first to know about shows and new music.
            </p>
            <Link
              to="/contact"
              data-testid="book-band-cta"
              className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-6 py-3 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
            >
              Booking Inquiries <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Music & Blog */}
      <section className="py-12 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="border border-[#0A1628]/12 bg-white p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-xl font-bold text-[#0A1628] mb-2">In the Captain's Log</h3>
              <p className="text-[#2D3142] text-sm">Thoughts on songwriting, Americana music, and the intersection of music and storytelling.</p>
            </div>
            <Link
              to="/blog?category=Music"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-6 py-3 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
            >
              Music Posts <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
